import { useState, useContext, useRef, useEffect } from 'react';
import { Box, Button, TextField, Typography, Divider, CircularProgress } from '@mui/material';
import axiosApiInstance from '@/api/shared/axios-instance';
import { Context } from '@/context';
import { REDUCERS } from '@/constants/reducers';
import URL_FACTORY from '@/helpers/url-helper';
import { notifyError, notifySuccess } from '@/notifications/notify';

/**
 * Componente compacto de login reutilizable
 * Incluye Google OneTap y formulario email/contraseña
 */
const LoginCompacto = ({ onSuccess, compact = false, marginTop = 0, ...props }) => {
    const { state, dispatch } = useContext(Context);
    const { user } = state;
    const googleButtonRef = useRef(null);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    // Inicializar Google OneTap
    useEffect(() => {
        if (user.logged) return;

        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client?hl=es';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);

        script.onload = () => {
            initializeGoogleOneTap();
        };

        return () => {
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
        };
    }, [user.logged]);

    const initializeGoogleOneTap = () => {
        if (typeof window === 'undefined' || !window.google) return;

        const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
        if (!clientId) return;

        window.google.accounts.id.initialize({
            client_id: clientId,
            callback: handleGoogleResponse,
            auto_select: false,
            cancel_on_tap_outside: true,
        });

        if (googleButtonRef.current) {
            window.google.accounts.id.renderButton(googleButtonRef.current, {
                theme: 'filled_black',
                size: compact ? 'medium' : 'large',
                width: '100%',
                text: 'signin_with',
                shape: 'rectangular',
                logo_alignment: 'left',
                locale: 'es',
            });
        }
    };

    const handleGoogleResponse = async (response) => {
        if (!response.credential) {
            notifyError('Error al autenticar con Google');
            return;
        }

        dispatch({ type: REDUCERS.SHOW_SPINNER });

        try {
            const result = await axiosApiInstance.post(URL_FACTORY.GoogleLoginUrl, {
                credential: response.credential,
            });

            if (result && result.data) {
                dispatch({
                    type: REDUCERS.LOGGED_IN_USER,
                    payload: {
                        ...result.data,
                        subscriptionType: result.data.subscriptionType || 0,
                        prevUrl: user.prevUrl,
                    },
                });

                notifySuccess(`¡Bienvenido, ${result.data.nickname ?? 'manager'}!`);

                if (onSuccess) {
                    onSuccess(result.data);
                }
            }
        } catch (error) {
            console.error('Error en Google Login:', error);
            notifyError('Error al iniciar sesión con Google');
        } finally {
            dispatch({ type: REDUCERS.HIDE_SPINNER });
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();

        if (!email || !password) {
            notifyError('Por favor, completa todos los campos');
            return;
        }

        setLoading(true);
        dispatch({ type: REDUCERS.SHOW_SPINNER });

        try {
            const result = await axiosApiInstance.post(URL_FACTORY.LoginUserUrl, {
                email,
                password,
            });

            if (result && result.data) {
                dispatch({
                    type: REDUCERS.LOGGED_IN_USER,
                    payload: {
                        ...result.data,
                        subscriptionType: result.data.subscriptionType || 0,
                        prevUrl: user.prevUrl,
                        refreshUrl: true,
                    },
                });

                notifySuccess(`¡Bienvenido, ${result.data.nickname ?? 'manager'}!`);

                if (onSuccess) {
                    onSuccess(result.data);
                }
            }
        } catch (error) {
            console.error('Error en login:', error);
            notifyError(error?.response?.data?.message || 'Credenciales incorrectas');
        } finally {
            setLoading(false);
            dispatch({ type: REDUCERS.HIDE_SPINNER });
        }
    };

    return (
        <Box sx={{ width: '100%', marginTop: marginTop }} {...props}>
            {/* Google OneTap Button */}
            <Box
                ref={googleButtonRef}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 2,
                    '& > div': {
                        width: '100% !important',
                    },
                }}
            />

            {/* Divider */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Divider sx={{ flex: 1 }} />
                <Typography variant="body2" sx={{ px: 2, color: 'text.secondary', fontSize: 12 }}>
                    o con email
                </Typography>
                <Divider sx={{ flex: 1 }} />
            </Box>

            {/* Formulario */}
            <Box component="form" onSubmit={handleLogin}>
                <TextField
                    fullWidth
                    size="small"
                    type="email"
                    label="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{ mb: 1.5 }}
                    autoComplete="email"
                />
                <TextField
                    fullWidth
                    size="small"
                    type="password"
                    label="Contraseña"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    sx={{ mb: 2 }}
                    autoComplete="current-password"
                />
                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading || !email || !password}
                    size={compact ? 'medium' : 'large'}
                >
                    {loading ? <CircularProgress size={24} /> : 'Iniciar sesión'}
                </Button>
            </Box>
        </Box>
    );
};

export default LoginCompacto;
